import { gql, useQuery } from "@apollo/client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  FilterIcon,
  Plus,
  SquareCheckBig,
  UserRound,
  X,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
  CompanyRole,
  ServicesFilterQueryQuery,
  ServicesFilterQueryQueryVariables,
} from "@/__generated__/graphql";
import { intersection } from "lodash";
import { Separator } from "@/components/ui/separator";
import ServiceDots from "@/components/common/ServiceDots";
import * as Switch from "@radix-ui/react-switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDebouncedSearchParam } from "@/hooks";
import { useUserContext } from "@/contexts/UserContextProvider";
import { PageViewModes } from "../pending.enum";
import SearchBar from "@/components/ui/search-bar";

function PendingFilters() {
  const [searchText, setSearchText] = useDebouncedSearchParam("shotName", 300);

  const { data, loading } = useQuery<
    ServicesFilterQueryQuery,
    ServicesFilterQueryQueryVariables
  >(SERVICES_FILTER_QUERY);

  if (loading && !data) {
    return null;
  }

  if (data) {
    const params: URLSearchParams = new URLSearchParams(window.location.search);
    const viewMode = params.get("tab");

    return (
      <div className="flex items-center gap-3 min-h-full">
        {(viewMode === PageViewModes.BidPackages || viewMode === null) && (
          <SearchBar
            placeholder="Shot Name"
            searchText={searchText}
            setSearchText={setSearchText}
            className="min-h-[38px]"
          />
        )}

        {(viewMode === PageViewModes.BidPackages || viewMode === null) && (
          <Filter data={data} />
        )}

        {viewMode === PageViewModes.Flash && <IsCompleted />}
      </div>
    );
  }
}

const Filter = ({ data }: { data: ServicesFilterQueryQuery }) => {
  const { role } = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [servicesUsed, setServiceUsed] = useState<string[]>([]);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isAddFiltersOpen, setIsAddFiltersOpen] = useState(false);

  const [showAwarded, setShowAwarded] = useState(false);

  const [showStatus, setShowStatus] = useState(false);
  const [showType, setShowType] = useState(false);

  const [status, setStatus] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);

  const filtersCount = useMemo(() => {
    return intersection(
      Array.from(searchParams.keys()),
      Object.values(PendingFilterOptions),
    ).length;
  }, [searchParams]);

  const applyFilters = () => {
    setIsFilterModalOpen(false);
    setSearchParams((params) => {
      params.delete(PendingFilterOptions.SERVICE_NAMES);

      if (servicesUsed.length > 0) {
        const services = servicesUsed.join(",");
        params.set(PendingFilterOptions.SERVICE_NAMES, services);
      } else {
        params.delete(PendingFilterOptions.SERVICE_NAMES);
      }

      if (showAwarded) params.set(PendingFilterOptions.AWARDED, "true");
      else params.delete(PendingFilterOptions.AWARDED);

      if (status) params.set(PendingFilterOptions.STATUS, status);
      else params.delete(PendingFilterOptions.STATUS);

      if (type) params.set(PendingFilterOptions.TYPE, type);
      else params.delete(PendingFilterOptions.TYPE);

      return params;
    });
  };

  const initFilters = useCallback(() => {
    setShowAwarded(!!searchParams.get(PendingFilterOptions.AWARDED));

    const services = searchParams.get(PendingFilterOptions.SERVICE_NAMES);
    const used = services?.split(",") || [];

    setServiceUsed(used);

    const statusParam = searchParams.get(PendingFilterOptions.STATUS) || null;
    setStatus(statusParam);
    if (statusParam) setShowStatus(true);

    const typeParam = searchParams.get(PendingFilterOptions.TYPE) || null;
    setType(typeParam);
    if (typeParam) setShowType(true);
  }, [searchParams]);

  const resetFilters = () => {
    setSearchParams((params) => {
      Object.values(PendingFilterOptions).forEach((value) => {
        params.delete(value);
      });
      return params;
    });

    setShowStatus(false);
    setShowType(false);

    setStatus(null);
    setType(null);

    initFilters();
  };

  useEffect(() => {
    initFilters();
  }, [initFilters, isFilterModalOpen]);

  const toggleServices = (serviceName: string) => {
    setServiceUsed((prev) =>
      prev.includes(serviceName)
        ? prev.filter((name) => name !== serviceName)
        : [...prev, serviceName],
    );
  };

  return (
    <Popover open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            "border rounded text-secondary-foreground dark:text-secondary-foreground max-h-[38px] min-h-[38px] border-box",
            {
              "text-service-paint dark:text-service-paint border-service-paint border-2 relative":
                filtersCount > 0,
            },
          )}
        >
          <FilterIcon
            color={
              filtersCount > 0
                ? "var(--color-service-paint)"
                : isFilterModalOpen
                  ? "var(--color-primary-foreground)"
                  : "var(--color-secondary-foreground)"
            }
          />
          Filter {filtersCount > 0 && `(${filtersCount})`}
          {filtersCount > 0 && (
            <>
              <div className="h-1 w-3" />
              <span
                className="absolute top-1/2 -translate-y-1/2 right-2 z-10 rounded-full pointer-events-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  resetFilters();
                }}
              >
                <X className="text-service-paint" />
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-fit p-0 min-w-100" align="end">
        <div className="p-4 text-sm">
          <div className="flex items-center justify-between">
            <div className="font-medium">Filter Tasks</div>

            <div
              className="text-xs text-secondary-foreground cursor-pointer"
              onClick={resetFilters}
            >
              Clear
            </div>
          </div>

          <div className="mt-4 text-secondary-foreground">
            <div className="mb-2 text-sm">Service filters</div>
            <div className="flex flex-wrap p-1.5 gap-3 text-sm">
              {data?.services.map((service) => {
                const isServiceUsed = servicesUsed.includes(service.name);

                return (
                  <div
                    key={service.id}
                    onClick={() => {
                      toggleServices(service.name);
                    }}
                    className="cursor-pointer"
                  >
                    <ServiceDots
                      services={service}
                      noWidth
                      square
                      transparent={!isServiceUsed}
                      colorText
                      colorBorder
                      bgOpacity30
                    />
                  </div>
                );
              })}
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <div className="my-2 text-sm">Other filters</div>

              {showStatus && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 pl-1 w-30">
                    <UserRound className="rotate-y-180" size={14} />
                    <span className="text-sm">Project Status</span>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex-1 border justify-between text-secondary-foreground font-normal w-full max-w-[200px]">
                        <span className="max-w-[80%] truncate">
                          {status != null
                            ? (() => {
                                const matched = Object.values(
                                  PendingStatusOptions,
                                ).find((opt) => opt.option === status);
                                if (!matched) return "All Statuses";

                                return role === CompanyRole.Vendor
                                  ? matched.name
                                  : matched.clientFilterName;
                              })()
                            : "All Statuses"}
                        </span>
                        <ChevronDown className="shrink-0 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      className="text-secondary-foreground max-h-36 "
                      style={{
                        minWidth: "var(--radix-dropdown-menu-trigger-width)",
                      }}
                      align="end"
                    >
                      <DropdownMenuItem
                        className={cn(
                          "cursor-pointer",
                          status == null && "bg-primary-foreground/10",
                        )}
                        onClick={() => setStatus(null)}
                      >
                        All Statuses
                      </DropdownMenuItem>
                      {(
                        Object.keys(PendingStatusOptions) as Array<
                          keyof typeof PendingStatusOptions
                        >
                      ).map((key) => (
                        <DropdownMenuItem
                          key={key}
                          className={cn(
                            "cursor-pointer",
                            status == PendingStatusOptions[key].option &&
                              "bg-primary-foreground/10",
                          )}
                          onClick={() =>
                            setStatus(PendingStatusOptions[key].option)
                          }
                        >
                          {role === CompanyRole.Vendor
                            ? PendingStatusOptions[key].name
                            : PendingStatusOptions[key].clientFilterName}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <X
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      setStatus(null);
                      setShowStatus(false);
                    }}
                  />
                </div>
              )}

              {showType && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 pl-1 w-30">
                    <Calendar className="rotate-y-180" size={14} />
                    <span className="text-sm">Project Type</span>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex-1 border justify-between text-secondary-foreground font-normal w-full max-w-[200px]">
                        <span className="truncate overflow-hidden whitespace-nowrap">
                          {type != null
                            ? (Object.values(TypeOptions).find(
                                (opt) => opt.option === type,
                              )?.name ?? "All Types")
                            : "All Types"}
                        </span>
                        <ChevronDown className="shrink-0 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      className="text-secondary-foreground max-h-36 "
                      style={{
                        width: "var(--radix-dropdown-menu-trigger-width)",
                      }}
                      align="end"
                    >
                      <DropdownMenuItem
                        className={cn(
                          "cursor-pointer",
                          type == null && "bg-primary-foreground/10",
                        )}
                        onClick={() => setType(null)}
                      >
                        All Types
                      </DropdownMenuItem>
                      {(
                        Object.keys(TypeOptions) as Array<
                          keyof typeof TypeOptions
                        >
                      ).map((key) => (
                        <DropdownMenuItem
                          key={key}
                          className={cn(
                            "cursor-pointer",
                            type == TypeOptions[key].option &&
                              "bg-primary-foreground/10",
                          )}
                          onClick={() => setType(TypeOptions[key].option)}
                        >
                          {TypeOptions[key].name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <X
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      setType(null);
                      setShowType(false);
                    }}
                  />
                </div>
              )}

              <div className="flex justify-between">
                <DropdownMenu
                  onOpenChange={(open) => setIsAddFiltersOpen(open)}
                >
                  {(!showStatus || !showType) && (
                    <DropdownMenuTrigger asChild>
                      <Button
                        className={cn(
                          "bg-tertiary text-secondary-foreground w-fit text-xs",
                          {
                            "text-primary-foreground": isAddFiltersOpen,
                          },
                        )}
                      >
                        <Plus />
                        Add filter
                        {isAddFiltersOpen ? <ChevronUp /> : <ChevronDown />}
                      </Button>
                    </DropdownMenuTrigger>
                  )}

                  <DropdownMenuContent
                    className="text-secondary-foreground w-50"
                    align="start"
                  >
                    {!showStatus && (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => {
                          setShowStatus(true);
                        }}
                      >
                        <UserRound />
                        Status
                      </DropdownMenuItem>
                    )}
                    {!showType && (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => {
                          setShowType(true);
                        }}
                      >
                        <Calendar />
                        Type
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="flex items-center border-1 p-2 rounded-lg gap-2">
                  <SquareCheckBig className="size-4" />
                  <span className="text-sm">Awarded</span>
                  <Switch.Root
                    checked={showAwarded}
                    onCheckedChange={() => setShowAwarded(!showAwarded)}
                    className="
                      relative inline-flex items-center w-8 h-4 
                      bg-[#F0F0F0] dark:bg-black 
                      rounded-[12px] p-[2px] 
                      transition-colors duration-200 ease-in-out
                      data-[state=checked]:bg-paint!
                    "
                  >
                    <Switch.Thumb
                      className="
                        block w-[12px] h-[12px] rounded-full 
                        bg-[#FF744D] shadow-[0px_1px_2px_0px_#1018280F,_0px_1px_3px_0px_#1018281A] 
                        transition-all duration-200 ease-in-out
                        translate-x-0 data-[state=checked]:translate-x-4
                        data-[state=checked]:bg-white
                      "
                    />
                  </Switch.Root>
                </div>
              </div>
            </div>
          </div>

          <Separator className="mt-5 mb-3" />

          <div className="flex items-center gap-4 w-fit ml-auto mr-0">
            <Button
              onClick={() => {
                setIsFilterModalOpen(false);
              }}
            >
              Cancel
            </Button>

            <Button
              className="bg-service-paint text-white hover:bg-service-paint/96"
              onClick={() => {
                applyFilters();
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const IsCompleted = () => {
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const isCompleted =
      searchParams.get(ShowCompletedOptions.SHOW_COMPLETED) === "true";
    setShowCompleted(isCompleted);
  }, [searchParams]);

  const handleToggle = (value: boolean) => {
    setShowCompleted(value);
    setSearchParams((params) => {
      if (value) {
        params.set(ShowCompletedOptions.SHOW_COMPLETED, "true");
      } else {
        params.delete(ShowCompletedOptions.SHOW_COMPLETED);
      }
      return params;
    });
  };

  return (
    <div className="flex items-center border-1 p-2 rounded-lg gap-2 bg-popover">
      <span className="text-sm">Show Completed</span>
      <Switch.Root
        checked={showCompleted}
        onCheckedChange={handleToggle}
        className="relative inline-flex items-center w-8 h-4 bg-[#F0F0F0] dark:bg-black rounded-[12px] p-[2px]"
      >
        <Switch.Thumb className="block w-[12px] h-[12px] rounded-full bg-[#FF744D] shadow-[0px_1px_2px_0px_#1018280F,_0px_1px_3px_0px_#1018281A] transition-transform duration-200 translate-x-0 data-[state=checked]:translate-x-4" />
      </Switch.Root>
    </div>
  );
};

export default PendingFilters;

const SERVICES_FILTER_QUERY = gql`
  query ServicesFilterQuery {
    services {
      id
      name
    }
  }
`;

export enum ShowCompletedOptions {
  SHOW_COMPLETED = "showCompleted",
}

export enum PendingFilterOptions {
  AWARDED = "awarded",
  SERVICE_NAMES = "serviceNames",
  STATUS = "status",
  TYPE = "type",
}

export const PendingStatusOptions = {
  PARTIALLY_AWARDED: {
    name: "Partially Awarded",
    clientFilterName: "Partially Awarded",
    option: "PARTIALLY_AWARDED",
  },
  PENDING_CLIENT_APPROVAL: {
    name: "Pending Client Approval",
    clientFilterName: "Pending Approval",
    option: "PENDING_CLIENT_APPROVAL",
  },
  PENDING_YOUR_BID: {
    name: "Pending Your Bid",
    clientFilterName: "Pending Bid",
    option: "PENDING_BID",
  },
};

export const TypeOptions = {
  NEW_BID: {
    name: "New Bids",
    option: "PENDING_BID",
  },
  STATE_CHANGES: {
    name: "State Changes",
    option: "STATE_CHANGES",
  },
};

export enum OtherFilterOptions {
  SHOT_NAME = "shotName",
  TASK_SHOT_IDS = "taskShotIds",
}
